<!-- A widget containing a NetworkGraph and controls for modifying the data/visualisation -->
<template>
    <div id="network-view">
        <network-graph
            id="network-graph"
            :height="graphHeight"
            :show-related="showRelated"
            :node-data="networkData"
            :top-data="topData"
            :manual-list="manualList"
            :remove-from-manual-list="removeFromManualList"
            :add-to-manual-list="addToManualList"
            :reload-data="reloadData"
            :show-manual-list-popup="() => this.$modal.show('dataList')"
        />
        <!-- Popup modal for adding manual network data -->
        <modal
            classes="dataModal"
            name="dataList"
            :adaptive="true"
            :clickToClose="false"
        >
            <network-data-select
                :on-cancel="cancelManualData"
                :on-confirm="acceptManualData"
                :is-artists="showArtists"
            />
        </modal>
        <div class="columns network-controls is-desktop" ref="controls">
            <!-- Switch for toggling between tracks and artists -->
            <div class="column">
                <div class="field">
                    <label
                        class="beforeSwitch"
                        @click="toggle('switchArtistsTracks')"
                    >
                        Tracks
                    </label>
                    <input
                        id="switchArtistsTracks"
                        ref="switchArtistsTracks"
                        type="checkbox"
                        class="switch is-rounded is-medium"
                        @change="
                            () => {
                                this.manualList = [];
                                reloadData();
                            }
                        "
                        v-model="showArtists"
                        checked
                    />
                    <label for="switchArtistsTracks">Artists</label>
                </div>
                <div class="field" v-if="!showArtists">
                    <button
                        id="btnPlaylist"
                        class="button is-primary"
                        @click="createPlaylist"
                    >
                        Create Playlist of Tracks
                    </button>
                </div>
            </div>
            <!-- Switch for toggling between manual data and top data -->
            <div class="column">
                <div class="field">
                    <label class="beforeSwitch" @click="toggle('switchTop')">
                        Manual Data
                    </label>
                    <input
                        id="switchTop"
                        ref="switchTop"
                        type="checkbox"
                        @change="reloadData"
                        v-model="topData"
                        class="switch is-rounded is-medium"
                        checked
                    />
                    <label for="switchTop">Top Data</label>
                </div>
            </div>
            <!-- Switch for toggling suggestions on and off -->
            <div class="column">
                <div class="field">
                    <input
                        id="switchRelated"
                        ref="switchRelated"
                        v-model="showRelated"
                        type="checkbox"
                        class="switch is-rounded is-medium"
                    />
                    <label for="switchRelated">Show Suggestions</label>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import NetworkGraph from "@/components/NetworkGraph";
import NetworkDataSelect from "@/components/NetworkDataSelect";
import api from "@/helpers/api";
import store from "@/store";

export default {
    name: "NetworkWidget",
    components: { NetworkDataSelect, NetworkGraph },
    data: function() {
        return {
            // The state for the suggestions toggle switch
            showRelated: true,
            // The data currently being shown on the network graph
            networkData: [],
            // The list of nodes to use for manual data (doesn't include suggestions)
            manualList: [],
            // The state for the manual/top data switch
            topData: true,
            // The state for the tracks/artist data switch
            showArtists: true
        };
    },
    computed: {
        // The height of the network graph in px
        graphHeight() {
            return this.$attrs.graphHeight ? this.$attrs.graphHeight : 600;
        },
        // The IDs of the nodes in manualList
        manualIDs() {
            return this.manualList.map(n => (n.identity ? n.identity : n.id));
        }
    },
    mounted() {
        this.reloadData();
    },
    methods: {
        // Toggle a given switch
        toggle(id) {
            this.$refs[id].checked = !this.$refs[id].checked;
        },
        // Get the user's top data graph from the API and set networkData = the result
        async getTopData() {
            let response = await api.get(
                this.showArtists ? "artists" : "tracks",
                {
                    params: {
                        breadth: 2,
                        // Accounts for the fact that artists tend to cycle resulting in fewer nodes
                        depth: this.showArtists ? 4 : 3
                    },
                    headers: {
                        Authorization: `Bearer ${store.state.accessToken}`
                    }
                }
            );
            this.networkData = response.data.graph;
        },
        // Get the graph from the API based on the data in manualList and set networkData = the result
        async getManualData() {
            let response = await api.get(
                this.showArtists ? "artists" : "tracks",
                {
                    params: {
                        ids: this.manualIDs,
                        breadth: 2,
                        // Accounts for the fact that artists tend to cycle resulting in fewer nodes
                        depth: this.showArtists ? 4 : 3
                    },
                    headers: {
                        Authorization: `Bearer ${store.state.accessToken}`
                    }
                }
            );
            this.networkData = response.data.graph;
            // Set the manualList equal to the (non-suggestions) nodes of the result
            this.manualList = [
                ...this.networkData.filter(
                    n => n.list_index != null && n.list_index !== -1
                )
            ];
        },
        // Re-fetch the graph data from the API
        reloadData() {
            if (this.topData) {
                this.getTopData();
            } else {
                this.getManualData();
            }
        },
        // Hide the 'add manual data' pop up modal
        cancelManualData() {
            this.$modal.hide("dataList");
        },
        // Add nodes to the manual list from the modal popup
        acceptManualData(data) {
            this.$modal.hide("dataList");

            // Only add nodes whose IDs are not already present
            data = data.filter(
                d =>
                    !this.manualIDs.some(id => id === d.id || id === d.identity)
            );
            this.manualList = this.manualList.concat(data);
        },
        // Add a given node to the manual list [with the onscreen add button on the graph overlay]
        addToManualList(d) {
            // Only add nodes whose IDs are not already present
            if (!this.manualIDs.some(id => id === d.id || id === d.identity)) {
                this.manualList.push((({ name, id }) => ({ name, id }))(d));
            }

            // Switch to manual data view
            this.$refs["switchTop"].checked = false;
            this.topData = false;
        },
        // Remove the node with the given id from the manual data list
        removeFromManualList(id) {
            const i = this.manualList.findIndex(
                n => n.identity === id || n.id === id
            );
            this.manualList.splice(i, 1);
        },
        async createPlaylist() {
            let response = await api.get("playlist", {
                headers: {
                    Authorization: `Bearer ${store.state.accessToken}`
                }
            });
            const status = response.data.error
                ? response.data.error.status
                : response.status;
            if (status === 200) {
                alert("Playlist of current tracks created!");
            } else {
                alert("Unable to create playlist!");
            }
        }
    }
};
</script>

<style lang="scss">
.beforeSwitch {
    position: relative;
    display: initial;
    font-size: 1.25rem;
    line-height: initial;
    padding-right: 0.6rem;
    padding-top: 0.2rem;
    cursor: pointer;
}

.network-controls {
    margin-top: auto !important;
    padding-left: 2rem;
    padding-right: 2rem;

    .column {
        text-align: center;
    }
}

.dataModal {
    background: #adadad !important;
    color: black !important;

    .tags-input .input,
    .label {
        color: black;

        &::placeholder {
            color: darkgray;
        }
    }

    .tag {
        background: #1db954;
    }

    .dropdown-item {
        color: black;
    }
}
</style>
